<script lang="ts" setup name="AdminScheduleRecipientsListSettingsNewForm">
import { ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { useField } from 'vee-validate';

import { FREQUENCY_TYPE } from '~/constants';

import Field from '~/components/Field.vue';
import RegistrationTradesSearch from '~/components/RegistrationTradesSearch.vue';

import type {
  IFormField,
  IRecipientListForm,
  IRegistrationHighlightedOption,
} from '~/types';

import FieldBaseErrors from './FieldBaseErrors.vue';

const props = defineProps<{
  form: IRecipientListForm;
  schema: Record<string, IFormField>;
  isEditMode?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: IRecipientListForm];
}>();

const tradeFormError = ref(false);
const form = useVModel(props, 'form', emit);

const { errors, value, errorMessage, handleChange } = useField<
  IRegistrationHighlightedOption[]
>(
  'tradelanes',
  (val) => {
    if (val.length) {
      return true;
    }
    return 'The trade is a mandatory field.';
  },
  {
    initialValue: form.value.tradelanes,
    validateOnValueUpdate: false,
    syncVModel: true,
  },
);
const onTradeLaneChange = () => {
  form.value.tradelanes = value.value;
  if (errorMessage.value) {
    handleChange(value.value);
  }
};

useField<string>(
  'start_date',
  (value) => {
    if (!value || form.value.frequency !== FREQUENCY_TYPE.ONCE) {
      return true;
    }

    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate >= today || 'Selected date is in the past';
  },
  {
    initialValue: props.form.startRecurringDate,
  },
);

if (props.isEditMode) {
  watch(
    () => form.value.frequency,
    (newFrequency, oldFrequency) => {
      if (newFrequency !== oldFrequency) {
        form.value.startRecurringDate = '';
      }
    },
  );
}
</script>

<template>
  <div class="max-w-form space-y-8">
    <div>
      <p>Select if you'd like to share the schedule on a recurring basis:</p>
      <Field class="mt-2" v-model="form.frequency" :field="schema.frequency" />
    </div>

    <div>
      <p v-if="form.frequency == FREQUENCY_TYPE.ONCE">Select the share date:</p>
      <p v-else>Select the start date for the recurring share:</p>

      <Field
        class="mt-2"
        v-model="form.startRecurringDate"
        :field="schema.startRecurringDate"
      />
    </div>

    <div>
      <p>Select trade:</p>
      <RegistrationTradesSearch
        class="mt-2"
        v-model="value"
        @update:model-value="onTradeLaneChange"
        :error="tradeFormError"
        @updateErrorState="tradeFormError = false"
        isFullWidth
        variant="minimal"
      />
      <FieldBaseErrors :errors class="mt-1" />
    </div>

    <div>
      <p>Enter sharing list name:</p>
      <Field class="mt-2" v-model="form.name" :field="schema.name" />
    </div>

    <div>
      <p>Enter message if needed:</p>
      <Field class="mt-2" v-model="form.message" :field="schema.message" />
    </div>
  </div>
</template>
